import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { PointerEventAdapter, PointerEventAdapterManager, PointerEventProps } from './PointerEventAdapter';
import { EventUtilsOptOut, mergeEventHandlers } from './ReactHelpers';

export { PointerEventProps };

/**
 * Wrap a DOM component to give it pointer events. Example that attaches a click to a div:
 *
 *    React.createElement(PointerEventWrapper, { pointerClick: () => console.log('click') },
 *      React.createElement('div')
 *    )
 *
 * When using, you can only have one child and it must render its props to a DOM component or events will not be attached.
 * If there is more than one child an exception will be thrown.
 */
export class PointerEventWrapper extends React.PureComponent<PointerEventProps, {}> {
  private pointerEventAdapter: PointerEventAdapter;

  public constructor(props: PointerEventProps) {
    super(props);
    this.pointerEventAdapter = PointerEventAdapterManager.adapterFactory();
  }

  public componentDidMount(): void {
    this.pointerEventAdapter.didMount?.(ReactDOM.findDOMNode(this) as Element, this.props);
  }

  public componentDidUpdate(): void {
    this.pointerEventAdapter.didUpdate?.(ReactDOM.findDOMNode(this) as Element, this.props);
  }

  public componentWillUnmount(): void {
    if (this.pointerEventAdapter.willUnmount) {
      this.pointerEventAdapter.willUnmount();
    }
  }

  public render(): JSX.Element {
    const eventAttributes = this.pointerEventAdapter.getHtmlAttributes(this.props);

    // reuse the child. It could be anything - div, ul, button, etc.
    const child = React.Children.only(this.props.children)!;
    const mergedEventAttributes: React.HTMLAttributes<HTMLElement> = {};
    Object.keys(eventAttributes).forEach((eventName: keyof typeof eventAttributes) => {
      mergedEventAttributes[eventName] = mergeEventHandlers(eventAttributes[eventName], child.props[eventName]);
    });

    /*
     * Although this class was originally created as a bridge to VizClient TableauEventHandler (i.e. `event-utils`),
     * we have found that it actually works much better to entirely opt-out of that infrastructure and use regular
     * React events. We add `EventUtilsOptout` here in order to make sure that anywhere still using `PointerEventWrapper`
     * doesn't actually end up using `event-utils`.
     */
    const clonedElement = React.cloneElement(child, { ...mergedEventAttributes, ...EventUtilsOptOut });
    return clonedElement;
  }
}
